import { useBrand } from "@branding/useBrand";

/**
 * Terms of Service. Written as a plain-language template a tenant firm adapts
 * to its own entity and jurisdiction. Deliberately not legal advice — it is a
 * starting point, not a filed contract.
 */
export function Terms() {
  const brand = useBrand();

  return (
    <section className="mk-section narrow mk-prose">
      <div className="mk-eyebrow">Legal</div>
      <h1 className="mk-h1">Terms of Service</h1>
      <p className="mk-updated">Last updated: 21 July 2026</p>

      <p>
        These Terms govern your access to and use of {brand.name} (the
        “Service”). By creating an organization or signing in, you agree to these
        Terms on behalf of yourself and the organization you represent. This is a
        plain-language template intended as a starting point; the operator of this
        deployment should adapt it to its own entity and jurisdiction, and it is
        not legal advice.
      </p>

      <h2>1. The Service</h2>
      <p>
        {brand.name} is a double-entry bookkeeping application for UAE finance
        teams. It helps you record transactions and produce a trial balance and
        financial statements with VAT and corporate-tax categorization. It is a
        record-keeping tool and does not provide accounting, tax, audit, or legal
        advice, and it is not a substitute for filing with the UAE Federal Tax
        Authority or advice from a qualified professional.
      </p>

      <h2>2. Accounts and access</h2>
      <ul>
        <li>You must provide accurate account information and keep your credentials confidential.</li>
        <li>You are responsible for activity under your account and for the roles you grant to members of your organization.</li>
        <li>You must be authorized to act for the organization whose books you manage.</li>
      </ul>

      <h2>3. Free trial and billing</h2>
      <p>
        Plans begin with a free trial. Prices displayed on the marketing site are
        samples for illustration and do not constitute an offer; no payment is
        collected through this website. Any paid subscription is governed by the
        billing terms presented at the time you subscribe.
      </p>

      <h2>4. Your data</h2>
      <p>
        You retain ownership of the financial data you enter. You grant us the
        limited rights needed to host and operate the Service for you. You can
        export your data at any time using the built-in CSV export. Our handling
        of personal data is described in the{" "}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>5. Acceptable use</h2>
      <ul>
        <li>Do not attempt to access data belonging to another organization or to circumvent authentication, roles, or tenant isolation.</li>
        <li>Do not probe, scan, or test the security of the Service without authorization, or interfere with its normal operation.</li>
        <li>Do not upload unlawful content or use the Service to violate applicable law.</li>
      </ul>

      <h2>6. Availability and accuracy</h2>
      <p>
        We work to keep the Service available and its calculations correct, but
        the Service is provided “as is” without warranties of any kind. You are
        responsible for reviewing your books and for the accuracy of what you
        file with any authority.
      </p>

      <h2>7. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, {brand.name} and its operator are
        not liable for indirect, incidental, or consequential damages, or for any
        loss arising from your reliance on outputs of the Service. Nothing here
        limits liability that cannot be limited by law.
      </p>

      <h2>8. Termination</h2>
      <p>
        You may stop using the Service and delete your organization’s data at any
        time. We may suspend or terminate access for breach of these Terms or to
        protect the Service and its users.
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update these Terms as the Service evolves. Material changes will be
        reflected by the “last updated” date above and, where appropriate, notified
        in-app.
      </p>

      <h2>10. Contact</h2>
      <p>
        Questions about these Terms can be raised from inside the app or with the
        operator of your {brand.name} deployment.
      </p>
    </section>
  );
}
